import { GtcrnWorkletNode, loadGtcrn, loadRnnoise, RnnoiseWorkletNode } from '@sapphi-red/web-noise-suppressor';
import gtcrnWasm from '@sapphi-red/web-noise-suppressor/gtcrn.wasm?url';
import gtcrnWorklet from '@sapphi-red/web-noise-suppressor/gtcrnWorklet.js?url';
import rnnoiseSimdWasm from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url';
import rnnoiseWasm from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url';
import rnnoiseWorklet from '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url';
import type { AudioProcessorOptions, Track, TrackProcessor } from 'livekit-client';
import howlWorklet from './howl-worklet.ts?worker&url';

export type DenoiseModel = 'rnnoise' | 'gtcrn';

/** RNNoise works on 48 kHz frames; one context for every mic track. */
let shared: AudioContext | null = null;
export function denoiseContext(): AudioContext {
  shared ??= new AudioContext({ sampleRate: 48_000 });
  return shared;
}

const binaries = new Map<DenoiseModel, Promise<ArrayBuffer>>();
const modules = new WeakMap<AudioContext, Set<DenoiseModel | 'howl'>>();

async function prepare(context: AudioContext, model: DenoiseModel): Promise<ArrayBuffer> {
  if (!binaries.has(model)) {
    binaries.set(model, model === 'rnnoise' ? loadRnnoise({ url: rnnoiseWasm, simdUrl: rnnoiseSimdWasm }) : loadGtcrn({ url: gtcrnWasm }));
  }
  const loaded = modules.get(context) ?? new Set();
  for (const name of [model, 'howl'] as const) {
    if (loaded.has(name)) continue;
    await context.audioWorklet.addModule(name === 'howl' ? howlWorklet : name === 'rnnoise' ? rnnoiseWorklet : gtcrnWorklet);
    loaded.add(name);
  }
  modules.set(context, loaded);
  return binaries.get(model)!;
}

/**
 * Machine-learning noise suppression in the browser (design §10: Krisp only exists on LiveKit
 * Cloud). The mic track runs through the model, then the howl guard (feedback between two speakers;
 * packages/client/src/howl.ts), before LiveKit publishes it.
 */
export function createDenoiser(model: DenoiseModel): TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> {
  let source: MediaStreamAudioSourceNode | null = null;
  let node: RnnoiseWorkletNode | GtcrnWorkletNode | null = null;
  let guard: AudioWorkletNode | null = null;

  const processor: TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> = {
    name: `novachat-${model}`,
    async init({ track, audioContext }) {
      const wasmBinary = await prepare(audioContext, model);
      source = audioContext.createMediaStreamSource(new MediaStream([track]));
      node = model === 'rnnoise' ? new RnnoiseWorkletNode(audioContext, { maxChannels: 1, wasmBinary }) : new GtcrnWorkletNode(audioContext, { maxChannels: 1, wasmBinary });
      guard = new AudioWorkletNode(audioContext, 'novachat-howl-guard', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
      const destination = audioContext.createMediaStreamDestination();
      source.connect(node).connect(guard).connect(destination);
      processor.processedTrack = destination.stream.getAudioTracks()[0];
    },
    async restart(options) {
      await processor.destroy();
      await processor.init(options);
    },
    async destroy() {
      source?.disconnect();
      node?.disconnect();
      node?.destroy();
      guard?.disconnect();
      guard?.port.close();
      source = null;
      node = null;
      guard = null;
    },
  };
  return processor;
}
